import { Component, OnInit, ViewChild, ViewEncapsulation, inject } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import { SortDirection } from "@angular/material/sort";
import { ActivatedRoute, Router } from "@angular/router";
import { CreateEditCustomerComponent } from "@components/create-edit-customer/create-edit-customer.component";
import { LoaderComponent } from "@components/layouts/loader/loader.component";
import { StatusMessageComponent } from "@components/layouts/status-message/status-message.component";
import { TableComponent } from "@components/layouts/table/table.component";
import { ViewCustomerComponent } from "@components/view-customer/view-customer.component";
import { Constants } from "@config/constants";
import { Messages } from "@config/messages";
import { ICustomer } from "@modals/api.modal";
import { ITableActionRow } from "@modals/app.modal";
import { AppService } from "@services/app.service";
import { AuthService } from "@services/auth.service";
import { HttpService } from "@services/http.service";
import { TranslateModule } from "@ngx-translate/core";

@Component({
  selector: "kzn-customers",
  standalone: true,
  imports: [
    LoaderComponent,
    StatusMessageComponent,
    ViewCustomerComponent,
    TableComponent,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    FormsModule,
    TranslateModule,
  ],
  templateUrl: "./customers.component.html",
  styleUrl: "./customers.component.scss",
  encapsulation: ViewEncapsulation.None,
})
export class CustomersComponent implements OnInit {
  @ViewChild("customersTable") customersTable?: TableComponent;

  private _httpService = inject(HttpService);
  private _activatedRoute = inject(ActivatedRoute);
  private _appService = inject(AppService);
  private _router = inject(Router);
  private _authService = inject(AuthService);

  isLoading = true;
  customers: Array<ICustomer> = [];
  isError = false;
  errorMessage = "";
  customerID = "";
  selectedCustomer!: ICustomer;
  tableSearchQuery = "";
  defaultSortColumnDef = "CreatedOn";
  defaultSortDirection: SortDirection = "desc";

  tableColumns = [
    { columnDef: "CustomerName", header: "customerName" },
    { columnDef: "Email", header: "email" },
    { columnDef: "Phone", header: "phone" },
    {
      columnDef: "LoginId",
      header: "username",
      cell: (row: ICustomer) => row?.Users?.[0]?.LoginId,
    },
    {
      columnDef: "IsEnabled",
      header: "active",
      cell: (row: ICustomer) => (row?.IsEnabled ? "yes" : "no"),
    },
    {
      columnDef: "CreatedOn",
      header: "creationDate",
      isDate: true,
      cell: (row: ICustomer) => this._appService.getFormattedDate(row.CreatedOn),
    },
    {
      columnDef: "UpdatedOn",
      header: "updationDate",
      isDate: true,
      cell: (row: ICustomer) => this._appService.getFormattedDate(row.UpdatedOn),
    },
  ];

  tableRowActions: Array<ITableActionRow> = [
    {
      icon: "remove_red_eye",
      color: "primary",
      title: "View",
      actionFn: (row: ICustomer) => {
        void this._router.navigate(["/customers", row.CustomerId]);
      },
    },
    {
      icon: "edit",
      color: "primary",
      title: "Edit",
      actionFn: (row: ICustomer) => {
        this.openCreateEditCustomerDialog(row);
      },
    },
  ];

  constructor(private dialog: MatDialog) {}

  ngOnInit(): void {
    if (!this._authService.isAdminUser()) {
      void this._router.navigate(["/requests"]);
      return;
    }

    this.getCustomers();

    this._activatedRoute.paramMap.subscribe((params) => {
      this.customerID = params.get("customerID") ?? params.get("UploadFileID") ?? "";
      this.syncSelectedCustomer();
    });
  }

  private syncSelectedCustomer(): void {
    if (!this.customerID || !this.customers.length) {
      return;
    }
    this.selectedCustomer = this.customers.find(
      (customer) => customer.CustomerId === this.customerID
    ) as ICustomer;
  }

  getCustomers(showLoader?: boolean): void {
    if ((showLoader && !this.customerID) || !this.customers.length) {
      this.isLoading = true;
    }
    this._httpService
      .get(this._httpService.createEndpoint(Constants.API_URI_CUSTOMER_GET_ALL))
      .subscribe(
        (response: any) => {
          this.customers = response.Data ?? [];
          this.syncSelectedCustomer();
          this.isLoading = false;
          this.isError = false;
          queueMicrotask(() => this.onTableSearch());
        },
        (errorResponse) => {
          this.isLoading = false;
          this.isError = true;
          this.errorMessage =
            errorResponse.error?.FailRules?.[0]?.Msg ||
            Messages.MSG_ERROR_UNEXPECTED;
        }
      );
  }

  openCreateEditCustomerDialog(customerDetails?: ICustomer): void {
    const dialogRef = this.dialog.open(CreateEditCustomerComponent, {
      data: {
        ...(customerDetails ? { customerDetails } : {}),
      },
      panelClass: "email-tpl-dialog-panel",
      backdropClass: "email-tpl-dialog-backdrop",
      autoFocus: false,
    });
    dialogRef.afterClosed().subscribe((data) => {
      if (data?.isSuccess) {
        this.getCustomers();
      }
    });
  }

  onTableSearch(): void {
    this.customersTable?.applyTextFilter(this.tableSearchQuery);
  }

  onCustomerUpdated(): void {
    this.getCustomers(true);
  }

  onCustomerSubscriptionCreated(): void {
    this.getCustomers(true);
  }

  navigateBackToList(): void {
    void this._router.navigate(["/customers"]);
  }
}
